import { inject, Injectable } from '@angular/core';
import { AuthFactoryService } from './auth-factory.service';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { LocalStorageService } from '../storage/local-storage.service';
import { DateUtils } from '../../shared/utils/date-utils';
import { UserClaims } from '@okta/okta-auth-js';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly authFactory = inject(AuthFactoryService);
  private readonly localStorageService = inject(LocalStorageService);

  /* Authenticated user info */
  private readonly userInfoSubject = new BehaviorSubject<UserClaims | null>(null);
  public userInfo$: Observable<UserClaims | null> = this.userInfoSubject.asObservable();

  /* Authentication state */
  private readonly authReadySubject = new BehaviorSubject<boolean>(false);
  public authReady$ = this.authReadySubject.asObservable();

  getAccessToken(): Observable<(string | null)[]> {
    return forkJoin([
      this.authFactory.getProvider('principal').getAccessToken(),
      this.authFactory.getProvider('oas').getAccessToken(),
      this.authFactory.getProvider('ms').getAccessToken(),
    ]);
  }

  async getScopes(): Promise<string[]> {
    const scopes = await Promise.all(
      this.authFactory.getAllProviders().flatMap((provider) => provider.getScopes()),
    );
    return scopes.flat();
  }

  async logout(): Promise<void> {
    await this.authFactory.getProvider('principal').logout();
    this.clear();
    this.localStorageService.remove('auth-workflow-state');
    this.userInfoSubject.next(null);
  }

  isPrincipalAuthenticated(): Promise<boolean> {
    return this.authFactory.getProvider('principal').isAuthenticated();
  }

  async isAuthenticated(): Promise<boolean> {
    return (
      await Promise.all(
        this.authFactory.getAllProviders().map((provider) => provider.isAuthenticated()),
      )
    ).every((isAuthenticated) => isAuthenticated);
  }

  isAnyAccessTokenExpired(tokens: (string | null)[]): boolean {
    return tokens.some((token) => this.isTokenExpired(token));
  }

  isTokenExpired(token: string | null): boolean {
    if (!token) {
      return false;
    }

    const tokenExpiresAt = this.extractTokenExpiresAt(token);
    return DateUtils.compareDateTime(new Date(), tokenExpiresAt * 1000) > 0;
  }

  async loadUserInfo(): Promise<void> {
    const idToken = await this.authFactory.getProvider('principal').getIdToken();
    if (idToken) {
      this.userInfoSubject.next(idToken.claims);
    }
  }

  private clear(): void {
    this.authFactory.getAllProviders().forEach((provider) => provider.clear());
  }

  private extractTokenExpiresAt(token: string): number {
    try {
      const parts = token.split('.');

      if (parts.length !== 3) {
        throw new Error('Invalid JWT token format');
      }

      const payload = parts[1];

      const base64 = payload.replaceAll('-', '+').replace('_', '/');
      const decodedPayload = JSON.parse(atob(base64));

      if (!decodedPayload.exp) {
        throw new Error('Token missing expiration claim');
      }

      return decodedPayload.exp;
    } catch (error) {
      console.error('Failed to decode token:', error);
      throw error;
    }
  }
}
